import axios from "axios";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import {
  AuthActionTypes,
  clearAccessToken,
  getAccessToken,
  refreshAccessToken,
} from "services/auth";
import { AuthWrapper } from "ui/modules/public/components/AuthWrapper";
import { LoginPage } from "ui/modules/public/pages/LoginPage";
import { ImpersonatePage } from "ui/modules/public/pages/ImpersonatePage";
import ImpersonationBar from "./ImpersonationBar/ImpersonationBar";
import { RegistrationPage } from "ui/modules/public/pages/RegistrationPage";
import { OfflinePage } from "ui/modules/public/pages/OfflinePage";
import { UnauthorizedPage } from "ui/modules/public/pages/UnauthorizedPage";
import { AppHome } from "./AppHome";
import { store } from "../redux";
import { Provider } from "react-redux";
import { SnackbarProvider } from "notistack";
import { snackbarOptions } from "ui/components/Snackbar";
import { ForgotPasswordPage } from "ui/modules/public/pages/ForgotPasswordPage";
import { getRefreshToken } from "services/auth/services";
import { DialogProvider } from "ui/modules/public/components/DialogProvider";
import {
  isNetworkError,
  checkApiHealth,
  SKIP_OFFLINE_CHECK_HEADER,
  navigateToOffline,
} from "services/api";
import { Routes as AppRoutes } from "ui/modules/public/navigation";
import { OfflineNavigatorBinder } from "ui/modules/public/components/OfflineNavigatorBinder";


// when we receive 401 error, we sign out the logged in user
function removeAccessToken(error: any) {
  clearAccessToken();
  store.dispatch({
    type: AuthActionTypes.SIGN_OUT,
    payload: error,
  });
}

function setAccessToken(
  accessToken: string | null,
  refreshToken: string | null
) {
  if (accessToken && refreshToken) {
    store.dispatch({
      type: AuthActionTypes.SIGN_IN_SUCCESS,
      payload: { accessToken: accessToken, refreshToken: refreshToken },
    });
  }
}

// Track if we're already refreshing to prevent duplicate refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;

/**
 * Sam poziv osvježavanja mora zaobići obradu 401.
 *
 * Bez ovoga bi njegov vlastiti 401 opet ušao u presretač, zatekao `refreshPromise` postavljen
 * i čekao obećanje koje čeka upravo taj zahtjev — zastoj iz kojeg se aplikacija ne vraća.
 */
const REFRESH_PATH = "/v1/users/tokens/refresh";

const isRefreshRequest = (error: any): boolean =>
  typeof error?.config?.url === "string" && error.config.url.includes(REFRESH_PATH);

// Deduplicate concurrent offline checks, and cache the last result briefly
// so a burst of failing requests doesn't trigger multiple health checks.
const OFFLINE_CACHE_MS = 5000;
let offlineCheckPromise: Promise<boolean> | null = null;
let lastOfflineResult: { offline: boolean; at: number } | null = null;

async function isTrulyOffline(): Promise<boolean> {
  if (navigator.onLine === false) return true;

  if (lastOfflineResult && Date.now() - lastOfflineResult.at < OFFLINE_CACHE_MS) {
    return lastOfflineResult.offline;
  }

  if (!offlineCheckPromise) {
    offlineCheckPromise = checkApiHealth()
      .then((healthy) => {
        const offline = !healthy;
        lastOfflineResult = { offline, at: Date.now() };
        return offline;
      })
      .finally(() => {
        offlineCheckPromise = null;
      });
  }
  return offlineCheckPromise;
}

axios.interceptors.response.use(
  async (response: any) => response,
  async (error: any) => {
    // If axios reports a network error, verify it's a real outage before redirecting.
    // A CORS failure or backend crash also looks like ERR_NETWORK but the API root may still respond.
    if (
      isNetworkError(error) &&
      window.location.pathname !== AppRoutes.OfflinePage &&
      !error.config?.headers?.[SKIP_OFFLINE_CHECK_HEADER]
    ) {
      if (await isTrulyOffline()) {
        // Prefer soft navigation so Redux state survives; fall back to hard
        // redirect if the binder isn't mounted yet (e.g., very early failure).
        if (!navigateToOffline()) {
          window.location.href = AppRoutes.OfflinePage;
        }
        return Promise.reject(error);
      }
    }

    // Handle 401 errors (Unauthorized)
    if (
      error.response !== undefined &&
      error.response.status === 401 &&
      !isRefreshRequest(error)
    ) {
      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();

      // If no tokens exist, immediately sign out
      if (!accessToken || !refreshToken) {
        removeAccessToken(error);
        return Promise.reject(error);
      }

      // If already refreshing, wait for the existing refresh to complete
      if (isRefreshing && refreshPromise) {
        try {
          await refreshPromise;
          // Retry the original request with new token
          return axios.request(error.config);
        } catch {
          removeAccessToken(error);
          return Promise.reject(error);
        }
      }

      // Start token refresh process
      isRefreshing = true;
      refreshPromise = refreshAccessToken(accessToken, refreshToken)
        .then((token) => {
          if (token && token.accessToken && token.refreshToken) {
            setAccessToken(token.accessToken, token.refreshToken);
            isRefreshing = false;
            refreshPromise = null;
            return token;
          } else {
            throw new Error('Invalid token response');
          }
        })
        .catch((err) => {
          isRefreshing = false;
          refreshPromise = null;
          removeAccessToken(error);
          throw err;
        });

      try {
        await refreshPromise;
        // Retry the original request with new token
        return axios.request(error.config);
      } catch {
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

function App() {
  // if there's an access token in storage, set it in redux and
  // fetch current user
  const accessToken = getAccessToken();
  const refreshToken = getRefreshToken();

  setAccessToken(accessToken, refreshToken);

  return (
    <>
      <Provider store={store}>
        <BrowserRouter>
          <OfflineNavigatorBinder />
          <SnackbarProvider {...snackbarOptions}>
            <AuthWrapper>
              <DialogProvider>
                <ImpersonationBar />
                <Routes>
                  <Route path={LoginPage.route} element={<LoginPage />} />
                  <Route path={ImpersonatePage.route} element={<ImpersonatePage />} />
                  <Route
                    path={RegistrationPage.route}
                    element={<RegistrationPage />}
                  />
                  <Route
                    path={ForgotPasswordPage.route}
                    element={<ForgotPasswordPage />}
                  />
                  <Route path={OfflinePage.route} element={<OfflinePage />} />
                  <Route path={UnauthorizedPage.route} element={<UnauthorizedPage />} />
                  <Route path={AppHome.route} element={<AppHome />} />
                </Routes>
              </DialogProvider>
            </AuthWrapper>
          </SnackbarProvider>
        </BrowserRouter>
      </Provider>
    </>
  );
}

export default App;
